import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FaUser, FaEnvelope, FaUserShield, FaSignOutAlt, FaMoon, FaSun, FaBars, FaTimes } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import toast from 'react-hot-toast';
import { USER_ROLES } from '../constants/roles';
import { chatService } from '../services/chatService';

const HeaderContainer = styled.header`
  background-color: ${props => props.theme.colors.surface};
  box-shadow: 0 2px 4px ${props => props.theme.colors.shadow};
  padding: 0.75rem 1.5rem;
  position: sticky;
  top: 0;
  z-index: 100;
  transition: background-color 0.3s ease;
`;

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  position: relative;
  
  @media (max-width: 1024px) {
    gap: 0.75rem;
  }
  
  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
  text-decoration: none;
  cursor: pointer;
  
  &:hover {
    opacity: 0.9;
  }
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const LogoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const LogoText = styled.h1`
  font-family: 'Georgia', serif;
  color: ${props => props.theme.colors.primary};
  font-size: 2rem;
  margin: 0;
  font-weight: 700;
  letter-spacing: 0.5px;
  
  @media (max-width: 1200px) {
    font-size: 1.75rem;
  }
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Tagline = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.75rem;
  margin: 0;
  font-style: italic;
  margin-top: -0.4rem;
  
  @media (max-width: 1200px) {
    font-size: 0.7rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.65rem;
    margin-top: -0.3rem;
  }
`;

const Navigation = styled.nav`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex: 1;
  justify-content: center;
  flex-wrap: nowrap;
  min-width: 0;
  
  @media (max-width: 1200px) {
    gap: 0.5rem;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: ${props => props.$isActive ? 'white' : props.theme.colors.primary};
  text-decoration: none;
  font-weight: 500;
  font-size: 0.8rem;
  padding: 0.4rem 0.6rem;
  border-radius: 20px;
  transition: all 0.3s ease;
  white-space: nowrap;
  background-color: ${props => props.$isActive ? props.theme.colors.primary : 'transparent'};
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    color: white;
  }
  
  @media (max-width: 1200px) {
    font-size: 0.75rem;
    padding: 0.35rem 0.5rem;
  }
`;

const UserActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-shrink: 0;
  
  @media (max-width: 1200px) {
    gap: 0.4rem;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.4rem;
  border-radius: 50%;
  transition: all 0.3s ease;
  position: relative;
  flex-shrink: 0;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    color: white;
  }
  
  @media (max-width: 1200px) {
    font-size: 1.1rem;
    padding: 0.35rem;
  }
`;

const Badge = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  background-color: #e74c3c;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: bold;
  border: 2px solid ${props => props.theme.colors.surface};
`;

const HamburgerButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'flex' : 'none'};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: ${props => props.theme.colors.surface};
    box-shadow: 0 4px 6px ${props => props.theme.colors.shadow};
    flex-direction: column;
    padding: 1rem;
    gap: 0.75rem;
    z-index: 99;
  }
`;

const MobileNavLink = styled(Link)`
  color: ${props => props.$isActive ? 'white' : props.theme.colors.primary};
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  background-color: ${props => props.$isActive ? props.theme.colors.primary : 'transparent'};
  text-align: center;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    color: white;
  }
`;

const MobileActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  padding-top: 0.5rem;
  border-top: 1px solid ${props => props.theme.colors.shadow};
  margin-top: 0.5rem;
`;

const MobileIconButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.75rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    color: white;
  }
`;


const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleProfileClick = () => {
    if (user) {
      navigate('/profile');
    } else {
      navigate('/login', { state: { from: '/profile' } });
    }
  };

  useEffect(() => {
    let interval;
    
    if (user) {
      // Load initial count
      loadUnreadCount();
      
      // Poll for updates every 30 seconds
      interval = setInterval(loadUnreadCount, 30000);
    } else {
      setUnreadCount(0);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [user]);

  // Refresh count when location changes
  useEffect(() => {
    if (user) {
      loadUnreadCount();
    }
  }, [location.pathname, user]);

  // Listen for messages being read
  useEffect(() => {
    const handleMessagesRead = () => {
      if (user) {
        loadUnreadCount();
      }
    };

    window.addEventListener('messagesRead', handleMessagesRead);
    return () => window.removeEventListener('messagesRead', handleMessagesRead);
  }, [user]);

  const loadUnreadCount = async () => {
    try {
      const count = await chatService.getUnreadCount(user.id);
      setUnreadCount(count);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const handleMessagesClick = () => {
    if (user) {
      navigate('/messages');
    } else {
      navigate('/login', { state: { from: '/messages' } });
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Signed out successfully');
      navigate('/');
      setMobileMenuOpen(false);
    } catch (error) {
      toast.error('Error signing out');
    }
  };

  const handleMobileNavClick = () => {
    setMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <HeaderContainer>
      <HeaderContent>
        <Logo to="/" onClick={() => setMobileMenuOpen(false)}>
          <LogoSection>
            <LogoText>Hustling</LogoText>
            <Tagline>Empowering and connecting entrepreneurs in South Africa</Tagline>
          </LogoSection>
        </Logo>
        
        <Navigation>
          <NavLink to="/find-hustle" $isActive={location.pathname === '/find-hustle'}>Find a Hustle</NavLink>
          <NavLink to="/share-hustle" $isActive={location.pathname === '/share-hustle'}>Share your Hustle</NavLink>
          <NavLink to="/find-gig" $isActive={location.pathname === '/find-gig'}>Find a Gig</NavLink>
          <NavLink to="/share-gig" $isActive={location.pathname === '/share-gig'}>Share your Gig</NavLink>
          <NavLink to="/about" $isActive={location.pathname === '/about'}>About us</NavLink>
          <NavLink to="/contact" $isActive={location.pathname === '/contact'}>Contact us</NavLink>
        </Navigation>

        <UserActions>
          <IconButton onClick={toggleTheme} title={theme.name === 'light' ? 'Dark Mode' : 'Light Mode'}>
            {theme.name === 'light' ? <FaMoon /> : <FaSun />}
          </IconButton>
          {user && (
            <IconButton onClick={handleMessagesClick} title="Messages">
              <FaEnvelope />
              {unreadCount > 0 && <Badge>{unreadCount > 99 ? '99+' : unreadCount}</Badge>}
            </IconButton>
          )}
          {user && (
            <IconButton onClick={handleSignOut} title="Sign Out">
              <FaSignOutAlt />
            </IconButton>
          )}
          <IconButton onClick={handleProfileClick} title={user ? 'My Profile' : 'Login'}>
            <FaUser />
          </IconButton>
        </UserActions>

        <HamburgerButton onClick={toggleMobileMenu}>
          {mobileMenuOpen ? <FaTimes /> : <FaBars />}
        </HamburgerButton>

        <MobileMenu $isOpen={mobileMenuOpen}>
          <MobileNavLink to="/find-hustle" $isActive={location.pathname === '/find-hustle'} onClick={handleMobileNavClick}>Find a Hustle</MobileNavLink>
          <MobileNavLink to="/share-hustle" $isActive={location.pathname === '/share-hustle'} onClick={handleMobileNavClick}>Share your Hustle</MobileNavLink>
          <MobileNavLink to="/find-gig" $isActive={location.pathname === '/find-gig'} onClick={handleMobileNavClick}>Find a Gig</MobileNavLink>
          <MobileNavLink to="/share-gig" $isActive={location.pathname === '/share-gig'} onClick={handleMobileNavClick}>Share your Gig</MobileNavLink>
          <MobileNavLink to="/about" $isActive={location.pathname === '/about'} onClick={handleMobileNavClick}>About us</MobileNavLink>
          <MobileNavLink to="/contact" $isActive={location.pathname === '/contact'} onClick={handleMobileNavClick}>Contact us</MobileNavLink>
          
          <MobileActions>
            <MobileIconButton onClick={() => { toggleTheme(); handleMobileNavClick(); }} title={theme.name === 'light' ? 'Dark Mode' : 'Light Mode'}>
              {theme.name === 'light' ? <FaMoon /> : <FaSun />}
            </MobileIconButton>
            {user && (
              <MobileIconButton onClick={() => { handleMessagesClick(); handleMobileNavClick(); }} title="Messages">
                <FaEnvelope />
                {unreadCount > 0 && <Badge>{unreadCount > 99 ? '99+' : unreadCount}</Badge>}
              </MobileIconButton>
            )}
            {user && (
              <MobileIconButton onClick={handleSignOut} title="Sign Out">
                <FaSignOutAlt />
              </MobileIconButton>
            )}
            <MobileIconButton onClick={() => { handleProfileClick(); handleMobileNavClick(); }} title={user ? 'My Profile' : 'Login'}>
              <FaUser />
            </MobileIconButton>
          </MobileActions>
        </MobileMenu>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;